import React, { ComponentType } from 'react';
import { Plugin } from './types';

// Global registry for managing script execution
class PluginScriptManager {
  private executedPlugins = new Set<string>();
  private pluginResults = new Map<string, any>(); // Store results for data-retrieval hooks
  private cleanupFunctions = new Map<string, () => void | Promise<void>>();
  private pluginRegistry: () => Plugin[] = () => [];
  private debug = false; // Disable verbose debugging by default

  // Set the plugin registry accessor
  setPluginRegistry(accessor: () => Plugin[]) {
    this.pluginRegistry = accessor;
    this.log('Plugin registry accessor set');
  }

  // Enable/disable debug logging
  setDebug(enabled: boolean) {
    this.debug = enabled;
  }

  private log(message: string, ...args: any[]) {
    if (this.debug) {
      console.log(`[PluginScript] ${message}`, ...args);
    }
  }

  // Get all plugins
  private getPlugins(): Plugin[] {
    try {
      const plugins = this.pluginRegistry();
      this.log(`Retrieved ${plugins.length} plugins from registry`);
      return plugins;
    } catch (error) {
      this.log('Error accessing plugin registry:', error);
      console.error('🔌 Debug: Error accessing plugin registry:', error);
      return [];
    }
  }

  // Execute a single plugin
  private async executePlugin(plugin: Plugin, specificHook?: string, args?: any, throwOnError: boolean = false): Promise<{ success: boolean; result?: any }> {
    const pluginKey = specificHook ? `${plugin.meta.name}:${specificHook}` : plugin.meta.name;
    
    // Check if already executed
    if (this.executedPlugins.has(pluginKey)) {
      // For data-retrieval hooks (contain ::), return stored result if available
      if (specificHook && specificHook.includes('::') && this.pluginResults.has(pluginKey)) {
        const storedResult = this.pluginResults.get(pluginKey);
        console.log(`🔌 Execute Debug: Returning cached result for ${pluginKey}:`, storedResult);
        return { success: true, result: storedResult };
      }
      console.log(`🔌 Execute Debug: Plugin ${pluginKey} already executed, no cached result`);
      return { success: true }; // Already executed
    }

    try {
      this.log(`Executing plugin: ${plugin.meta.name}${specificHook ? ` (hook: ${specificHook})` : ''}`);
      
      let result;
      
      if (specificHook) {
        // Check for hooks only
        if (plugin.hooks?.[specificHook]) {
          const hookFunction = plugin.hooks[specificHook];
          this.log(`Executing hook: ${specificHook} in plugin: ${plugin.meta.name}`);
          console.log(`🔌 Execute Debug: Hook function found for ${specificHook} in ${plugin.meta.name}`);
          
          if (typeof hookFunction === 'function') {
            try {
              console.log(`🔌 Execute Debug: Calling hook function with args:`, args);
              result = hookFunction(args);
              console.log(`🔌 Execute Debug: Hook function returned:`, result);
              
              if (result instanceof Promise) {
                console.log(`🔌 Execute Debug: Result is a Promise, awaiting...`);
                result = await result;
                console.log(`🔌 Execute Debug: Promise resolved to:`, result);
              }
            } catch (hookError) {
              console.log(`🔌 Execute Debug: Error during hook execution:`, hookError);
              this.log(`Error during hook execution:`, hookError);
              throw hookError;
            }
          } else {
            console.log(`🔌 Execute Debug: Hook ${specificHook} in plugin ${plugin.meta.name} is not a function`);
            this.log(`Hook ${specificHook} in plugin ${plugin.meta.name} is not a function`);
            return { success: false };
          }
        } else {
          console.log(`🔌 Execute Debug: No hook found for: ${specificHook} in plugin: ${plugin.meta.name}`);
          this.log(`No hook found for: ${specificHook} in plugin: ${plugin.meta.name}`);
          return { success: false };
        }
      } else if (plugin.execute) {
        // Execute main execute function
        result = plugin.execute(args);
        if (result instanceof Promise) {
          result = await result;
        }
      } else {
        this.log(`No main execute function found for plugin: ${plugin.meta.name}`);
        return { success: false };
      }

      // Store cleanup function if provided
      if (plugin.cleanup) {
        this.cleanupFunctions.set(plugin.meta.name, plugin.cleanup);
      }

      this.executedPlugins.add(pluginKey);
      
      // Store result for data-retrieval hooks (contain ::) - only cache non-undefined values
      if (specificHook && specificHook.includes('::') && result !== undefined) {
        this.pluginResults.set(pluginKey, result);
        console.log(`🔌 Execute Debug: Cached result for ${pluginKey}:`, result);
      } else if (specificHook && specificHook.includes('::') && result === undefined) {
        console.log(`🔌 Execute Debug: Not caching undefined result for ${pluginKey}`);
      }
      
      this.log(`Plugin executed: ${plugin.meta.name}${specificHook ? ` (hook: ${specificHook})` : ''}`);
      
      console.log(`🔌 Execute Debug: Final result before return:`, result);
      console.log(`🔌 Execute Debug: typeof final result:`, typeof result);
      console.log(`🔌 Execute Debug: result === undefined:`, result === undefined);
      
      return { success: true, result };
    } catch (error) {
      // Log the error details - this is important for debugging
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.log(`Error executing plugin ${plugin.meta.name}${specificHook ? ` hook: ${specificHook}` : ''}:`, error);
      
      // Also show a visible error message (not just in debug mode)
      if (specificHook) {
        console.error(`🔌 Plugin Hook Execution Error: "${plugin.meta.name}.${specificHook}" - ${errorMessage}`);
      } else {
        console.error(`🔌 Plugin Execution Error: "${plugin.meta.name}" - ${errorMessage}`);
      }
      
      // Re-throw the error if throwOnError is true
      if (throwOnError) {
        throw error;
      }
      
      return { success: false };
    }
  }

  // Hook system for lifecycle events
  async hookScript(
    hook: string, 
    args?: any,
    options?: {
      slot?: string;
      pluginName?: string;
      force?: boolean;
      throwOnError?: boolean; // New option to throw on first error
      returnIndividualResults?: boolean; // Return per-plugin results instead of grouped
      resultAggregator?: (results: any[]) => any; // Custom aggregation function
    }
  ): Promise<{ 
    executed: string[]; 
    errors: Array<{ plugin: string; error: Error }>; 
    result?: any; // Aggregated result (default)
    results?: Array<{ plugin: string; result: any }>; // Individual results (when returnIndividualResults = true)
  }> {
    const { 
      slot, 
      pluginName, 
      force = false, 
      throwOnError = false,
      returnIndividualResults = false,
      resultAggregator
    } = options || {};
    const plugins = this.getPlugins();
    const errors: Array<{ plugin: string; error: Error }> = [];
    const executed: string[] = [];
    const individualResults: Array<{ plugin: string; result: any }> = [];

    this.log(`Hook triggered: ${hook}`, { slot, pluginName, args });

    // Always log for custom hooks to help debug issues
    if (hook.includes('::') && !['before::enter', 'app::init', 'plugin::load', 'slot::enter', 'before::leave', 'app::cleanup', 'app::start', 'plugin::execute'].includes(hook)) {
      console.log(`🔌 Custom Hook: "${hook}" triggered`);
      console.log(`🔌 Custom Hook: Total plugins available:`, plugins.length);
      console.log(`🔌 Custom Hook: Plugin names:`, plugins.map(p => p.meta.name));
      console.log(`🔌 Custom Hook: All available hooks across plugins:`, 
        plugins.flatMap(p => p.hooks ? Object.keys(p.hooks).map(h => `${p.meta.name}:${h}`) : [])
      );
    }

    // Only log detailed debug info if debug mode is enabled
    if (this.debug) {
      console.log(`🔌 Hook Debug: Hook triggered: ${hook}`, { slot, pluginName, args });
      console.log(`🔌 Hook Debug: Total plugins available:`, plugins.length);
      console.log(`🔌 Hook Debug: Plugin names:`, plugins.map(p => p.meta.name));
    }

    // Filter plugins based on criteria
    let targetPlugins = plugins.filter(plugin => {
      const hasHooks = plugin.execute || plugin.hooks;
      const matchesName = !pluginName || plugin.meta.name === pluginName;
      
      // Always log for custom hooks to help debug
      if (hook.includes('::') && !['before::enter', 'app::init', 'plugin::load', 'slot::enter', 'before::leave', 'app::cleanup', 'app::start', 'plugin::execute'].includes(hook)) {
        console.log(`🔌 Custom Hook Debug: Plugin ${plugin.meta.name} - hasHooks: ${hasHooks}, matchesName: ${matchesName}`);
        if (plugin.hooks) {
          console.log(`🔌 Custom Hook Debug: Plugin ${plugin.meta.name} available hooks:`, Object.keys(plugin.hooks));
          console.log(`🔌 Custom Hook Debug: Looking for hook "${hook}" in plugin ${plugin.meta.name}:`, !!plugin.hooks[hook]);
        } else {
          console.log(`🔌 Custom Hook Debug: Plugin ${plugin.meta.name} has no hooks object`);
        }
      }
      
      if (this.debug) {
        console.log(`🔌 Hook Debug: Plugin ${plugin.meta.name} - hasHooks: ${hasHooks}, matchesName: ${matchesName}`);
        if (plugin.hooks) {
          console.log(`🔌 Hook Debug: Plugin ${plugin.meta.name} available hooks:`, Object.keys(plugin.hooks));
        }
      }
      return hasHooks && matchesName;
    });

    if (this.debug) {
      console.log(`🔌 Hook Debug: Filtered to ${targetPlugins.length} target plugins`);
    }

    // Execute plugins based on hook type
    switch (hook) {
      case 'before::enter':
      case 'app::init':
      case 'plugin::load':
      case 'slot::enter':
      case 'before::leave':
      case 'app::cleanup':
      case 'app::start': // Add app::start to standard lifecycle hooks
        if (this.debug) {
          console.log(`🔌 Hook Debug: Standard lifecycle hook: ${hook}`);
        }
        break;
      
      case 'plugin::execute':
        targetPlugins = targetPlugins.filter(p => p.meta.name === pluginName);
        if (this.debug) {
          console.log(`🔌 Hook Debug: Execute specific plugin, filtered to: ${targetPlugins.length}`);
        }
        break;
      
      default:
        // Custom hook - look for plugins that have this specific hook
        const beforeFilter = targetPlugins.length;
        console.log(`🔌 Custom Hook: Looking for "${hook}" in ${beforeFilter} plugins`);
        
        targetPlugins.forEach(p => {
          console.log(`🔌 Custom Hook: Plugin "${p.meta.name}" has hooks:`, p.hooks ? Object.keys(p.hooks) : 'none');
          console.log(`🔌 Custom Hook: Plugin "${p.meta.name}" has hook "${hook}":`, !!(p.hooks && p.hooks[hook]));
        });
        
        targetPlugins = targetPlugins.filter(p => p.hooks && p.hooks[hook]);
        
        console.log(`🔌 Custom Hook: Filtered from ${beforeFilter} to ${targetPlugins.length} plugins for hook "${hook}"`);
        console.log(`🔌 Custom Hook: Final plugins:`, targetPlugins.map(p => p.meta.name));
        
        if (this.debug) {
          console.log(`🔌 Hook Debug: Custom hook ${hook}, filtered from ${beforeFilter} to ${targetPlugins.length}`);
        }
        break;
    }

    if (this.debug) {
      console.log(`🔌 Hook Debug: Final target plugins for ${hook}:`, targetPlugins.map(p => p.meta.name));
    }

    if (targetPlugins.length === 0) {
      if (this.debug) {
        console.log(`🔌 Hook Debug: No target plugins found for hook: ${hook}`);
      }
      return returnIndividualResults 
        ? { executed, errors, results: individualResults }
        : { executed, errors, result: undefined };
    }

    // Execute all target plugins
    if (this.debug) {
      console.log(`🔌 Hook Debug: Executing ${targetPlugins.length} plugins for hook: ${hook}`);
    }
    
    if (throwOnError) {
      // Use sequential execution and throw on first error
      for (const plugin of targetPlugins) {
        const hasHook = plugin.hooks && plugin.hooks[hook];
        const pluginKey = hasHook ? `${plugin.meta.name}:${hook}` : plugin.meta.name;
        
        const shouldExecute = force || !this.executedPlugins.has(pluginKey);
        
        if (this.debug) {
          console.log(`🔌 Hook Debug: Plugin ${plugin.meta.name} - hasHook: ${!!hasHook}, shouldExecute: ${shouldExecute}, alreadyExecuted: ${this.executedPlugins.has(pluginKey)}`);
        }
        
        if (!shouldExecute) {
          if (this.debug) {
            console.log(`🔌 Hook Debug: Skipping ${plugin.meta.name} - already executed`);
          }
          continue;
        }
        
        try {
          const execution = await this.executePlugin(plugin, hasHook ? hook : undefined, args, true);
          console.log(`🔌 Result Debug: Plugin ${plugin.meta.name} executePlugin returned:`, execution);
          
          if (execution.success) {
            executed.push(plugin.meta.name);
            // Always log execution results for debugging
            console.log(`🔌 Result Debug: Plugin ${plugin.meta.name} execution success, result:`, execution.result);
            console.log(`🔌 Result Debug: typeof result:`, typeof execution.result);
            console.log(`🔌 Result Debug: result === undefined:`, execution.result === undefined);
            console.log(`🔌 Result Debug: result === null:`, execution.result === null);
            
            if (execution.result !== undefined) {
              individualResults.push({ plugin: plugin.meta.name, result: execution.result });
              if (this.debug) {
                console.log(`🔌 Hook Debug: Captured result from ${plugin.meta.name}:`, execution.result);
              }
            } else {
              // Always log when result is undefined to help debug
              console.log(`🔌 Result Debug: Plugin ${plugin.meta.name} returned undefined result`);
              if (this.debug) {
                console.log(`🔌 Hook Debug: No result from ${plugin.meta.name} (result was undefined)`);
              }
            }
            if (this.debug) {
              console.log(`🔌 Hook Debug: Successfully executed plugin: ${plugin.meta.name}`);
            }
          } else {
            console.log(`🔌 Result Debug: Plugin ${plugin.meta.name} execution failed (success: false)`);
          }
          // Note: if executePlugin returns false with throwOnError=true, it means no error occurred
          // but the plugin had no execute function or hook to run
        } catch (error) {
          // Error was already logged by executePlugin, just re-throw the original error
          throw error;
        }
      }
    } else {
      // Use Promise.allSettled for concurrent execution (existing behavior)
      const executionResults = await Promise.allSettled(
        targetPlugins.map(plugin => {
          // Check if plugin has specific hook
          const hasHook = plugin.hooks && plugin.hooks[hook];
          const pluginKey = hasHook ? `${plugin.meta.name}:${hook}` : plugin.meta.name;
          
          const shouldExecute = force || !this.executedPlugins.has(pluginKey);
          
          if (this.debug) {
            console.log(`🔌 Hook Debug: Plugin ${plugin.meta.name} - hasHook: ${!!hasHook}, shouldExecute: ${shouldExecute}, alreadyExecuted: ${this.executedPlugins.has(pluginKey)}`);
          }
          
          if (!shouldExecute) {
            if (this.debug) {
              console.log(`🔌 Hook Debug: Skipping ${plugin.meta.name} - already executed`);
            }
            // Still call executePlugin to get cached result for data-retrieval hooks
            return this.executePlugin(plugin, hasHook ? hook : undefined, args, false);
          }
          
          return this.executePlugin(plugin, hasHook ? hook : undefined, args, false);
        })
      );

      executionResults.forEach((result, index) => {
        const plugin = targetPlugins[index];
        if (result.status === 'fulfilled' && result.value) {
          const execution = result.value;
          if (execution.success) {
            executed.push(plugin.meta.name);
            if (execution.result !== undefined) {
              individualResults.push({ plugin: plugin.meta.name, result: execution.result });
              if (this.debug) {
                console.log(`🔌 Hook Debug: Captured result from ${plugin.meta.name}:`, execution.result);
              }
            } else if (this.debug) {
              console.log(`🔌 Hook Debug: No result from ${plugin.meta.name} (result was undefined)`);
            }
            if (this.debug) {
              console.log(`🔌 Hook Debug: Successfully executed plugin: ${plugin.meta.name}`);
            }
          }
        } else if (result.status === 'rejected') {
          const error = result.reason instanceof Error ? result.reason : new Error(String(result.reason));
          
          // Always log hook errors, not just in debug mode
          console.error(`🔌 Plugin Hook Error: Plugin "${plugin.meta.name}" hook "${hook}" failed:`, error.message);
          
          if (this.debug) {
            console.error(`🔌 Hook Debug: Full error details for ${plugin.meta.name}:`, error);
          }
          
          errors.push({
            plugin: plugin.meta.name,
            error
          });
        }
      });
    }

    // Always log the summary (concise)
    this.log(`Hook ${hook} completed. Executed: ${executed.length}, Errors: ${errors.length}`);
    if (executed.length > 0) {
      console.log(`🔌 Hook ${hook} executed: ${executed.join(', ')}`);
    }
    
    // Always show error summary if there are errors
    if (errors.length > 0) {
      console.warn(`🔌 Hook ${hook} had ${errors.length} error(s): ${errors.map(e => e.plugin).join(', ')}`);
    }
    
    // Return individual results or aggregated result based on option
    if (returnIndividualResults) {
      return { executed, errors, results: individualResults };
    } else {
      // Aggregate results by default
      let aggregatedResult: any;
      
      // Always log what we're aggregating for debugging
      console.log(`🔌 Aggregation Debug: individualResults.length = ${individualResults.length}`);
      console.log(`🔌 Aggregation Debug: individualResults =`, individualResults);
      
      // For debugging: also try with force=true to bypass "already executed" check
      if (individualResults.length === 0 && executed.length > 0 && hook.includes('::')) {
        console.log(`🔌 Aggregation Debug: No results but plugins executed - this might be due to "already executed" check`);
        console.log(`🔌 Aggregation Debug: Consider using { force: true } option to re-execute plugins`);
      }
      
      if (individualResults.length > 0) {
        const resultValues = individualResults.map(r => r.result);
        
        // Always log result values for debugging
        console.log(`🔌 Aggregation Debug: resultValues =`, resultValues);
        console.log(`🔌 Aggregation Debug: resultValues.length = ${resultValues.length}`);
        
        // Debug logging for result aggregation
        if (this.debug) {
          console.log(`🔌 Hook Debug: Aggregating ${resultValues.length} results:`, resultValues);
          console.log(`🔌 Hook Debug: Result types:`, resultValues.map(r => typeof r));
          console.log(`🔌 Hook Debug: All arrays?`, resultValues.every(r => Array.isArray(r)));
          console.log(`🔌 Hook Debug: All objects?`, resultValues.every(r => typeof r === 'object' && r !== null && !Array.isArray(r)));
        }
        
        if (resultAggregator && typeof resultAggregator === 'function') {
          // Use custom aggregator
          aggregatedResult = resultAggregator(resultValues);
          console.log(`🔌 Aggregation Debug: Custom aggregator result:`, aggregatedResult);
          if (this.debug) {
            console.log(`🔌 Hook Debug: Custom aggregator result:`, aggregatedResult);
          }
        } else {
          // Default aggregation logic
          if (resultValues.length === 1) {
            // Single result - return as is
            aggregatedResult = resultValues[0];
            console.log(`🔌 Aggregation Debug: Single result returned as-is:`, aggregatedResult);
            if (this.debug) {
              console.log(`🔌 Hook Debug: Single result returned as-is:`, aggregatedResult);
            }
          } else if (resultValues.every(r => Array.isArray(r))) {
            // All arrays - flatten them
            aggregatedResult = resultValues.flat();
            console.log(`🔌 Aggregation Debug: Arrays flattened:`, aggregatedResult);
            if (this.debug) {
              console.log(`🔌 Hook Debug: Arrays flattened:`, aggregatedResult);
            }
          } else if (resultValues.every(r => typeof r === 'object' && r !== null && !Array.isArray(r))) {
            // All objects - merge them
            aggregatedResult = Object.assign({}, ...resultValues);
            console.log(`🔌 Aggregation Debug: Objects merged:`, aggregatedResult);
            if (this.debug) {
              console.log(`🔌 Hook Debug: Objects merged:`, aggregatedResult);
            }
          } else {
            // Mixed types or primitives - return as array
            aggregatedResult = resultValues;
            console.log(`🔌 Aggregation Debug: Mixed types returned as array:`, aggregatedResult);
            if (this.debug) {
              console.log(`🔌 Hook Debug: Mixed types returned as array:`, aggregatedResult);
            }
          }
        }
      } else {
        console.log(`🔌 Aggregation Debug: No results to aggregate`);
        if (this.debug) {
          console.log(`🔌 Hook Debug: No results to aggregate`);
        }
      }
      
      console.log(`🔌 Aggregation Debug: Final aggregated result:`, aggregatedResult);
      if (this.debug) {
        console.log(`🔌 Hook Debug: Final aggregated result:`, aggregatedResult);
      }
      
      return { executed, errors, result: aggregatedResult };
    }
  }

  // Cleanup plugins
  async cleanupPlugins(slot?: string, pluginName?: string): Promise<void> {
    const toCleanup = Array.from(this.executedPlugins).filter(name => {
      if (pluginName) return name === pluginName;
      if (slot) {
        const plugin = this.getPlugins().find(p => p.meta.name === name);
        return plugin?.meta.slot === slot;
      }
      return true;
    });

    for (const name of toCleanup) {
      try {
        const cleanupFn = this.cleanupFunctions.get(name);
        if (cleanupFn) {
          this.log(`Cleaning up plugin: ${name}`);
          const result = cleanupFn();
          if (result instanceof Promise) {
            await result;
          }
          this.cleanupFunctions.delete(name);
        }
        this.executedPlugins.delete(name);
        // Also clear cached results
        this.pluginResults.delete(name);
      } catch (error) {
        this.log(`Error cleaning up plugin ${name}:`, error);
      }
    }
  }

  // Get execution status
  getStatus() {
    const plugins = this.getPlugins();
    const hookPlugins = plugins.filter(p => 
      p.execute || p.hooks
    );
    
    return {
      totalPlugins: plugins.length,
      hookPlugins: hookPlugins.length,
      executedPlugins: this.executedPlugins.size,
      executed: Array.from(this.executedPlugins),
      available: hookPlugins.map(p => p.meta.name)
    };
  }

  // Public method to get all plugins for debugging
  getAllPlugins(): Plugin[] {
    return this.getPlugins();
  }

  // Force re-execution of plugins
  async forceExecute(slot?: string, pluginName?: string): Promise<any> {
    return this.hookScript('plugin::execute', null, { slot, pluginName, force: true });
  }

  // Get component plugins for rendering
  getComponentPlugins(slot?: string): Plugin[] {
    const plugins = this.getPlugins();
    return plugins.filter(plugin => {
      const hasComponent = plugin.Component || plugin.Components || 
                          (plugin.slots && Object.values(plugin.slots).some(s => s.components));
      const matchesSlot = !slot || (plugin.slots && plugin.slots[slot]?.components);
      const isEnabled = plugin.meta.enabled !== false;
      
      return hasComponent && (slot ? matchesSlot : true) && isEnabled;
    });
  }

  // Get specific component from plugin
  getPluginComponent(pluginName: string, componentName?: string): ComponentType<any> | null {
    const plugins = this.getPlugins();
    const plugin = plugins.find(p => p.meta.name === pluginName);
    
    if (!plugin) return null;
    
    // Check slots first (new structure)
    if (plugin.slots) {
      for (const [slotName, slotContent] of Object.entries(plugin.slots)) {
        if (slotContent.components) {
          if (componentName && slotContent.components[componentName]) {
            return slotContent.components[componentName];
          } else if (!componentName && Object.keys(slotContent.components).length > 0) {
            // Return first component if no specific name requested
            return Object.values(slotContent.components)[0];
          }
        }
      }
    }
    
    // Fallback to legacy structure
    if (componentName && plugin.Components && plugin.Components[componentName]) {
      return plugin.Components[componentName];
    }
    
    if (!componentName && plugin.Component) {
      return plugin.Component;
    }
    
    return null;
  }

  // Get all components from a plugin
  getPluginComponents(pluginName: string): { [name: string]: ComponentType<any> } {
    const plugins = this.getPlugins();
    const plugin = plugins.find(p => p.meta.name === pluginName);
    
    if (!plugin) return {};
    
    const components: { [name: string]: ComponentType<any> } = {};
    
    // Check slots first (new structure)
    if (plugin.slots) {
      for (const [slotName, slotContent] of Object.entries(plugin.slots)) {
        if (slotContent.components) {
          for (const [componentName, component] of Object.entries(slotContent.components)) {
            components[`${slotName}::${componentName}`] = component;
          }
        }
      }
    }
    
    // Add main component as 'default' if exists
    if (plugin.Component) {
      components.default = plugin.Component;
    }
    
    // Add named components
    if (plugin.Components) {
      Object.assign(components, plugin.Components);
    }
    
    return components;
  }

  // Get components for a specific slot from all plugins
  getSlotComponents(slotName: string): { [pluginName: string]: { [componentName: string]: ComponentType<any> } } {
    const plugins = this.getPlugins();
    const slotComponents: { [pluginName: string]: { [componentName: string]: ComponentType<any> } } = {};

    for (const plugin of plugins) {
      const pluginComponents: { [componentName: string]: ComponentType<any> } = {};
      
      // Check new slots structure
      if (plugin.slots?.[slotName]?.components) {
        Object.assign(pluginComponents, plugin.slots[slotName].components);
      }
      
      if (Object.keys(pluginComponents).length > 0) {
        slotComponents[plugin.meta.name] = pluginComponents;
      }
    }
    
    return slotComponents;
  }

  // Get all available slots from all plugins
  getAvailableSlots(): string[] {
    const plugins = this.getPlugins();
    const slots = new Set<string>();

    for (const plugin of plugins) {
      // Add slots from new structure
      if (plugin.slots) {
        Object.keys(plugin.slots).forEach(slot => slots.add(slot));
      }
    }

    return Array.from(slots);
  }

  // Get all available component names across all plugins
  getAllComponentNames(): { pluginName: string; componentName: string; slot?: string }[] {
    const plugins = this.getPlugins();
    const components: { pluginName: string; componentName: string; slot?: string }[] = [];
    
    plugins.forEach(plugin => {
      // Add components from slots structure
      if (plugin.slots) {
        Object.entries(plugin.slots).forEach(([slotName, slotContent]) => {
          if (slotContent.components) {
            Object.keys(slotContent.components).forEach(componentName => {
              components.push({
                pluginName: plugin.meta.name,
                componentName,
                slot: slotName
              });
            });
          }
        });
      }
      
      // Add main component (legacy)
      if (plugin.Component) {
        components.push({
          pluginName: plugin.meta.name,
          componentName: 'default'
        });
      }
      
      // Add named components (legacy)
      if (plugin.Components) {
        Object.keys(plugin.Components).forEach(componentName => {
          components.push({
            pluginName: plugin.meta.name,
            componentName
          });
        });
      }
    });
    
    return components;
  }

  // Get all available hooks from all plugins
  getAvailableHooks(): string[] {
    const plugins = this.getPlugins();
    const hooks = new Set<string>();
    
    plugins.forEach(plugin => {
      // Add hooks only
      if (plugin.hooks) {
        Object.keys(plugin.hooks).forEach(hook => hooks.add(hook));
      }
    });

    return Array.from(hooks);
  }
}

// Global instance
const scriptManager = new PluginScriptManager();

// Expose globally for debugging
if (typeof window !== 'undefined') {
  (window as any).pluginScriptManager = scriptManager;
  (window as any).hookScript = (hook: string, args?: any, options?: any) => 
    scriptManager.hookScript(hook, args, options);
  (window as any).enableHookDebug = (enabled: boolean = true) => {
    scriptManager.setDebug(enabled);
    console.log(`🔌 Hook debugging ${enabled ? 'enabled' : 'disabled'}`);
  };
  (window as any).debugPluginHooks = () => {
    console.log('=== Plugin Hook Debug Info ===');
    const status = scriptManager.getStatus();
    console.log('Status:', status);
    console.log('Available hooks:', scriptManager.getAvailableHooks());
    console.log('Executed plugins:', status.executed);
    console.log('All plugins with hooks:');
    const allPlugins = scriptManager.getAllPlugins();
    allPlugins.forEach(plugin => {
      if (plugin.hooks || plugin.execute) {
        console.log(`- ${plugin.meta.name}:`, {
          hasExecute: !!plugin.execute,
          hooks: plugin.hooks ? Object.keys(plugin.hooks) : []
        });
      }
    });
    console.log('===============================');
  };
}

// Utility functions that can be called from anywhere
// Overloaded signatures for better TypeScript support
export function hookScript(
  hook: string, 
  args?: any, 
  options?: {
    slot?: string;
    pluginName?: string;
    force?: boolean;
    throwOnError?: boolean;
    returnIndividualResults: true;
    resultAggregator?: (results: any[]) => any;
  }
): Promise<{ executed: string[]; errors: Array<{ plugin: string; error: Error }>; results: Array<{ plugin: string; result: any }> }>;

export function hookScript(
  hook: string, 
  args?: any, 
  options?: {
    slot?: string;
    pluginName?: string;
    force?: boolean;
    throwOnError?: boolean;
    returnIndividualResults?: false;
    resultAggregator?: (results: any[]) => any;
  }
): Promise<{ executed: string[]; errors: Array<{ plugin: string; error: Error }>; result?: any }>;

export function hookScript(
  hook: string, 
  args?: any, 
  options?: {
    slot?: string;
    pluginName?: string;
    force?: boolean;
    throwOnError?: boolean;
    returnIndividualResults?: boolean;
    resultAggregator?: (results: any[]) => any;
  }
) {
  return scriptManager.hookScript(hook, args, options);
}

export const cleanupPlugins = (slot?: string, pluginName?: string) => 
  scriptManager.cleanupPlugins(slot, pluginName);

export const getPluginStatus = () => scriptManager.getStatus();

export const getComponentPlugins = (slot?: string) => scriptManager.getComponentPlugins(slot);

export const getAvailableHooks = () => scriptManager.getAvailableHooks();

export const forceExecutePlugins = (slot?: string, pluginName?: string) => 
  scriptManager.forceExecute(slot, pluginName);

export const enablePluginDebug = (enabled: boolean = true) => 
  scriptManager.setDebug(enabled);

// Hook to initialize the script manager with plugin registry
export const usePluginScriptManager = (pluginRegistryAccessor: () => Plugin[]) => {
  React.useEffect(() => {
    scriptManager.setPluginRegistry(pluginRegistryAccessor);
  }, [pluginRegistryAccessor]);
};

// React hook for easy usage in components
export const useHookScript = () => {
  return {
    hookScript,
    cleanupPlugins,
    getStatus: getPluginStatus,
    getComponentPlugins,
    getAvailableHooks,
    forceExecute: forceExecutePlugins,
    enableDebug: enablePluginDebug,
    // Add missing methods
    getPluginComponent: (pluginName: string, componentName?: string) => 
      scriptManager.getPluginComponent(pluginName, componentName),
    getPluginComponents: (pluginName: string) => 
      scriptManager.getPluginComponents(pluginName),
    getSlotComponents: (slotName: string) => 
      scriptManager.getSlotComponents(slotName),
    getAvailableSlots: () => 
      scriptManager.getAvailableSlots(),
    getAllComponentNames: () => 
      scriptManager.getAllComponentNames()
  };
};
